import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import { toast } from 'react-toastify';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import authService from '../services/authService';
import mainIcon from '../assets/images/mainIcon.jpeg';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [visibility, setVisibility] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  const toggleVisibility = () => {
    setVisibility((prev) => !prev);
  };

  const getErrorMessage = (error) => {
    // Handle specific error cases
    if (error?.response?.status === 401) {
      return 'Invalid email or password. Please try again.';
    }
    if (error?.response?.status === 404) {
      return 'Account not found. Please check your email.';
    }
    if (error?.response?.status === 403) {
      return 'Your account has been disabled. Please contact support.';
    }
    if (error?.response?.status === 429) {
      return 'Too many login attempts. Please try again later.';
    }
    if (error?.response?.status === 500) {
      return 'Server error. Please try again later.';
    }
    if (error?.message?.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    // Default error message
    return error?.error || 'An error occurred during login. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (!email || !password) {
      setError('Please enter both email and password');
      setLoading(false);
      return;
    }

    try {
      // Call the login service
      const response = await authService.login(email, password);
      
      // Update the authentication state in the context
      login();
      
      // Show success message
      toast.success('Login successful!');
      
      // Wait for authentication state to be properly set
      const waitForAuth = () => {
        return new Promise((resolve) => {
          const checkAuth = () => {
            if (!isLoading) {
              resolve();
            } else {
              setTimeout(checkAuth, 50);
            }
          };
          checkAuth();
        });
      };
      
      await waitForAuth();
      
      // Navigate to admin dashboard after auth state is ready
      setTimeout(() => {
        navigate('/admin');
      }, 100);
      
    } catch (error) {
      console.error('Error during login:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <body className="bg-primary min-h-screen pt-12 md:pt-20 pb-6 px-2 md:px-0">
      <main className="bg-white max-w-lg mx-auto p-8 md:p-12 my-10 rounded-lg shadow-2xl">
        <section className="mt-10 h-4/6">
          <img src={mainIcon} alt="Logo" className='w-52 m-auto'/>

          <form className="flex flex-col" onSubmit={handleSubmit} noValidate method="POST" action="#">
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
            <div className="mb-4">
              <label htmlFor="email" className="block mb-1 text-gray-700">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`input input-bordered w-full ${error ? 'border-red-500' : ''}`}
                disabled={loading}
                placeholder="Enter your email"
              />
            </div>
            <div className="mb-4">
              <label htmlFor="password" className="block mb-1 text-gray-700">
                Password
              </label>
              <div className="relative">
                <input
                  type={visibility ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`input input-bordered w-full pr-10 ${error ? 'border-red-500' : ''}`}
                  disabled={loading}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={toggleVisibility}
                  className="absolute right-4 top-4 text-gray-600"
                >
                  {visibility ? (
                    <span role="img" aria-label="Hide Password">
                      <MdVisibilityOff/>
                    </span>
                  ) : (
                    <span role="img" aria-label="Show Password">
                      <MdVisibility/>
                    </span>
                  )}
                </button>
              </div>
            </div>
            <button 
              className={`bg-primary hover:bg-slatext-slate-500 text-white font-bold py-2 rounded shadow-lg hover:shadow-xl transition duration-200 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              type="submit" 
              disabled={loading}
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>
          <div className='flex justify-center'>
            <Link to={'/forgot-password'}>
              <button type='button' className='text-primary m-4 text-center content-center'>Forgot Password</button>
            </Link>
          </div>
        </section>
      </main>

      <div className="max-w-lg mx-auto text-center mt-12 mb-6">
        <p className="text-white mb-2">Adira Logistics System &copy; {new Date().getFullYear()} </p>
        <div className="flex justify-center space-x-4">
          <Link to="/privacy-policy" className="text-white hover:text-gray-200 text-sm underline">
            Privacy Policy
          </Link>
          <Link to="/terms-of-service" className="text-white hover:text-gray-200 text-sm underline">
            Terms of Service
          </Link>
        </div>
      </div>
    </body>
  );
};

export default Login;
